import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { DayCell } from '../day-cell'
import {
    ChevronLeftGlyph,
    ChevronRightGlyph,
    ClockGlyph,
    GLYPH_COLORS,
} from '../picker-icons/pickerGlyphs'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { WeekdayLabel } from '../weekday-label'
import {
    buildCalendarMatrix,
    type CalendarDay,
    formatMonthTitle,
    isSameDay,
    isWithinRange,
    startOfDay,
    weekdayLabels,
    type WeekStart,
} from './calendar.utils'

export type CalendarSheetMode = 'default' | 'date-range'

export type CalendarSheetProps = {
    /** Any date inside the month to render. */
    month: Date
    mode?: CalendarSheetMode
    /** Highlighted day in `default` mode. */
    selectedDate?: Date | null
    /** Range bounds in `date-range` mode (order-independent). */
    rangeStart?: Date | null
    rangeEnd?: Date | null
    /** Day to outline as "today". Defaults to none (deterministic for stories). */
    today?: Date | null
    weekStartsOn?: WeekStart
    /** Renders the bottom time-toggle affordance (clock icon). */
    showTime?: boolean
    /** Override the default `October 2020`-style title (e.g. localized). */
    monthLabel?: string
    /** Predicate to disable specific days. */
    isDateDisabled?: (date: Date) => boolean
    onPrevMonth?: () => void
    onNextMonth?: () => void
    onSelectDate?: (date: Date) => void
    onTimePress?: () => void
    className?: string
}

const headerIcon = 'size-6 items-center justify-center'

type DayFlags = {
    isSelected: boolean
    isToday: boolean
    isOutsideMonth: boolean
    isRangeStart: boolean
    isRangeEnd: boolean
    isInRange: boolean
    isDisabled: boolean
    isBandMember: boolean
}

/**
 * Month calendar grid matching the Figma redesign (AOS-616). Presentational and
 * controlled: the parent owns the visible `month` and selection; this renders
 * the header, weekday row and a fixed 6-week grid of {@link DayCell}s. Supports
 * single-date (`default`) and `date-range` selection — in range mode the
 * in-range run is painted as a continuous highlighted band per week row.
 */
export const CalendarSheet: React.FC<CalendarSheetProps> = ({
    month,
    mode = 'default',
    selectedDate,
    rangeStart,
    rangeEnd,
    today,
    weekStartsOn = 0,
    showTime = true,
    monthLabel,
    isDateDisabled,
    onPrevMonth,
    onNextMonth,
    onSelectDate,
    onTimePress,
    className,
}) => {
    const weeks = buildCalendarMatrix(month, weekStartsOn)
    const headers = weekdayLabels(weekStartsOn)
    const isRange = mode === 'date-range'

    const normStart =
        rangeStart && rangeEnd
            ? startOfDay(rangeStart).getTime() <= startOfDay(rangeEnd).getTime()
                ? rangeStart
                : rangeEnd
            : rangeStart
    const normEnd =
        rangeStart && rangeEnd
            ? startOfDay(rangeStart).getTime() <= startOfDay(rangeEnd).getTime()
                ? rangeEnd
                : rangeStart
            : rangeEnd

    const flagsFor = ({ date, isOutsideMonth }: CalendarDay): DayFlags => {
        const isRangeStart = isRange && isSameDay(date, normStart)
        const isRangeEnd = isRange && isSameDay(date, normEnd)
        const isBandMember = isRange && isWithinRange(date, normStart, normEnd)
        return {
            isOutsideMonth,
            isToday: isSameDay(date, today),
            isSelected: !isRange && isSameDay(date, selectedDate),
            isRangeStart,
            isRangeEnd,
            isInRange: isBandMember && !isRangeStart && !isRangeEnd,
            isDisabled: isDateDisabled?.(date) ?? false,
            isBandMember,
        }
    }

    const renderCell = (item: CalendarDay, flags: DayFlags) => (
        <DayCell
            key={item.date.getTime()}
            day={item.day}
            isSelected={flags.isSelected}
            isToday={flags.isToday}
            isOutsideMonth={flags.isOutsideMonth}
            isInRange={flags.isInRange}
            isRangeStart={flags.isRangeStart}
            isRangeEnd={flags.isRangeEnd}
            isDisabled={flags.isDisabled}
            onPress={onSelectDate ? () => onSelectDate(item.date) : undefined}
        />
    )

    // Group consecutive in-range days of a week into a single highlighted band
    // so the gaps between cells are filled (matching Figma), rounding only the
    // true range endpoints.
    const renderWeek = (week: CalendarDay[], weekIndex: number) => {
        const items: React.ReactNode[] = []
        let band: { node: React.ReactNode; isStart: boolean; isEnd: boolean }[] = []

        const flushBand = () => {
            if (band.length === 0) {
                return
            }
            const roundLeft = band[0].isStart
            const roundRight = band[band.length - 1].isEnd
            const rounding = twMerge(roundLeft ? 'rounded-l' : '', roundRight ? 'rounded-r' : '')
            items.push(
                <Box
                    key={`band-${weekIndex}-${items.length}`}
                    className={twMerge('flex-row gap-2 bg-background-highlight', rounding)}
                >
                    {band.map(b => b.node)}
                </Box>,
            )
            band = []
        }

        week.forEach(item => {
            const flags = flagsFor(item)
            if (flags.isBandMember) {
                band.push({
                    node: renderCell(item, flags),
                    isStart: flags.isRangeStart,
                    isEnd: flags.isRangeEnd,
                })
            } else {
                flushBand()
                items.push(renderCell(item, flags))
            }
        })
        flushBand()

        return (
            <Box key={`week-${weekIndex}`} className='flex-row gap-2'>
                {items}
            </Box>
        )
    }

    return (
        <Box className={twMerge('flex-col items-center gap-4 py-6 w-[216px]', className)}>
            <Box className='flex-col items-start gap-4 w-full'>
                {/* Header: month navigation */}
                <Box className='flex-row items-center justify-between w-full'>
                    <Pressable
                        className={headerIcon}
                        onPress={onPrevMonth}
                        accessibilityRole='button'
                        accessibilityLabel='Previous month'
                    >
                        <ChevronLeftGlyph size={24} color={GLYPH_COLORS.default} />
                    </Pressable>
                    <Text size='P3' weight='medium' className='flex-1 text-center'>
                        {monthLabel ?? formatMonthTitle(month)}
                    </Text>
                    <Pressable
                        className={headerIcon}
                        onPress={onNextMonth}
                        accessibilityRole='button'
                        accessibilityLabel='Next month'
                    >
                        <ChevronRightGlyph size={24} color={GLYPH_COLORS.default} />
                    </Pressable>
                </Box>

                {/* Grid */}
                <Box className='flex-col items-start gap-2 w-full'>
                    <Box className='flex-row gap-2'>
                        {headers.map(label => (
                            <WeekdayLabel key={label} label={label} />
                        ))}
                    </Box>
                    {weeks.map((week, index) => renderWeek(week, index))}
                </Box>
            </Box>

            {showTime ? (
                <Pressable
                    className='size-6 items-center justify-center'
                    onPress={onTimePress}
                    accessibilityRole='button'
                    accessibilityLabel='Switch to time'
                >
                    <ClockGlyph size={24} color={GLYPH_COLORS.accent} />
                </Pressable>
            ) : null}
        </Box>
    )
}

CalendarSheet.displayName = 'CalendarSheet'
